import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import axios from "axios";
import { Endpoints } from "../../Global/Endpoints";

const EMPTY_TEXT = "-";

const normalizeWebsite = (website) => {
  if (!website) {
    return "";
  }

  return /^https?:\/\//i.test(website) ? website : `https://${website}`;
};

const formatPhoneHref = (phone) => `${phone || ""}`.replace(/[^\d+]/g, "");

const decodeHtml = (value) => {
  if (!value || typeof value !== "string") {
    return value || "";
  }

  return value
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
};

const formatValue = (value) => {
  const normalizedValue = decodeHtml(`${value ?? ""}`.trim());

  if (!normalizedValue) {
    return EMPTY_TEXT;
  }

  return normalizedValue;
};

const hasDisplayValue = (value) => {
  const normalizedValue = decodeHtml(`${value ?? ""}`.trim());
  return Boolean(normalizedValue);
};

const normalizeProviderResponse = (responseData) => {
  if (Array.isArray(responseData)) {
    return responseData;
  }

  if (!responseData || typeof responseData !== "object") {
    return [];
  }

  const preferredCollections = [
    responseData.ProviderDirectory,
    responseData.ProviderDirectoryDetails,
    responseData.ProviderDetails,
    responseData.Data,
    responseData.Items,
  ];

  const matchingCollection = preferredCollections.find((item) => Array.isArray(item) && item.length > 0);
  if (matchingCollection) {
    return matchingCollection;
  }

  return [responseData];
};

const ProviderDirectory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const token = localStorage.getItem("AccessToken");
  const contract = useSelector((state) => state.userData);
  const ssoData = useSelector((state) => state.sso.ssoUserData);
  const isHighmarkPortal = ssoData?.Portal === "Highmark";

  const [providerData, setProviderData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const routeState = useMemo(() => location.state || {}, [location.state]);
  const userData = contract?.userData || {};
  const memberId = userData.MemberID || "";
  const contractId = userData.ContractIdentified || "";
  const deviceType = isHighmarkPortal ? "HighmarkPortal" : "PatientPortal";
  const emailId = userData.EmailID || "";

  useEffect(() => {
    const fetchProviderDirectory = async () => {
      const params = {
        MemberID: memberId,
        Contract:
          contractId || "",
        DeviceType: deviceType,
        EmailID: emailId,
        Address:
          routeState.Address || "",
        GroupName:
          routeState.GroupName || "",
        Language: userData.Language || "",
      };

      try {
        setLoading(true);
        setLoadError("");

        const response = await axios.post(
          Endpoints.LoadProviderDirectory,
          { _Params: JSON.stringify(params) },
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );

        setProviderData(normalizeProviderResponse(response.data));
      } catch (error) {
        console.error("Error loading provider directory:", error);
        setProviderData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProviderDirectory();
  }, [contractId, deviceType, emailId, memberId, queryParams, routeState, token]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const providers = useMemo(
    () =>
      providerData.map((provider, providerIndex) => {
        const header = provider.ProviderHeader || provider;
        const phone = decodeHtml(`${header.PhoneNumber || header.Phone || ""}`.trim());
        const website = formatValue(header.Website);
        const email = formatValue(header.Email);

        return {
          id: `${header.ProviderName || header.Name || header.GroupName || "provider"}-${providerIndex}`,
          summary: {
            name: formatValue(header.ProviderName || header.Name || header.GroupName),
            gender: decodeHtml(`${header.Gender ?? ""}`.trim()),
            street: formatValue(header.Address || header.Street),
            phone,
            website,
            email,
            websiteUrl: website && website !== EMPTY_TEXT ? normalizeWebsite(website) : "",
            phoneHref: phone ? formatPhoneHref(phone) : "",
          },
          attributes: (provider.ProviderFields || [])
            .filter((item) => hasDisplayValue(item.Answer))
            .map((item, index) => ({
              id: `${item.Question || "field"}-${providerIndex}-${index}`,
              label: formatValue(item.Question),
              answer: formatValue(item.Answer),
              subFields: Array.isArray(item.SubFields)
                ? item.SubFields
                    .filter((subField) => hasDisplayValue(subField.SubAnswer))
                    .map((subField, subIndex) => ({
                      id: `${subField.SubQuestion || "subfield"}-${providerIndex}-${index}-${subIndex}`,
                      label: formatValue(subField.SubQuestion),
                      answer: formatValue(subField.SubAnswer),
                    }))
                : [],
            })),
        };
      }),
    [providerData]
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading"></div>
        <h2 className="loading-text">{t("loading")}</h2>
      </div>
    );
  }

  return (
    <>
      {isHighmarkPortal ? (
        <div className="col-md-12 d-flex inner-page-heading-wrapper provider-directory-heading">
          <button className="win-back-btn" onClick={handleBackClick}>
            <span>
              <i className="bi bi-chevron-left"></i>
            </span>
          </button>
          <h2 className="page-heading">Provider Directory</h2>
        </div>
      ) : (
        <div className="col-md-12 d-flex inner-page-heading-wrapper provider-directory-heading">
          <button className="win-back-btn" onClick={handleBackClick}>
            <span>
              <i className="bi bi-chevron-left"></i>
              {t("Back")}
            </span>
          </button>
          <h2 className="page-heading">Provider Directory</h2>
        </div>
      )}

      <div className="provider-directory-shell">
        {providers.length > 0 ? (
          <>
            {providers.map((provider) => (
              <section
                key={provider.id}
                className="provider-directory-summary provider-directory-card"
              >
                <div className="provider-directory-header-card">
                  <h2 className="provider-directory-name">{provider.summary.name}</h2>

                  <div className="provider-directory-detail-row">
                    <div>
                      {provider.summary.gender && (
                        <p className="provider-directory-info-text">{provider.summary.gender}</p>
                      )}
                      <p className="provider-directory-info-text">{provider.summary.street}</p>
                      {provider.summary.phone && provider.summary.phone !== EMPTY_TEXT && (
                        <a
                          href={`tel:${provider.summary.phoneHref}`}
                          className="provider-directory-link provider-directory-phone-link"
                        >
                          {provider.summary.phone}
                        </a>
                      )}
                    </div>

                    <div className="provider-directory-contact">
                      {provider.summary.website && provider.summary.website !== EMPTY_TEXT && (
                        <a
                          href={provider.summary.websiteUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="provider-directory-link provider-directory-website-link"
                        >
                          {provider.summary.website}
                        </a>
                      )}

                      {provider.summary.email && provider.summary.email !== EMPTY_TEXT && (
                        <a
                          href={`mailto:${provider.summary.email}`}
                          className="provider-directory-link provider-directory-email-link"
                        >
                          {provider.summary.email}
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                <div className="provider-directory-grid">
                  {provider.attributes.map((item) => (
                    <div key={item.id}>
                      <h3 className="provider-directory-item-title">{item.label}</h3>
                      <div className="provider-directory-field-stack">
                        <div className="provider-directory-pill">
                          <div className="provider-directory-pill-value">
                            <div>{item.answer}</div>
                          </div>
                        </div>
                        {item.subFields.map((subField) => (
                          <div key={subField.id} className="provider-directory-subfield-stack">
                            <h4 className="provider-directory-subfield-title">{subField.label}</h4>
                            <div className="provider-directory-subfield-pill">
                              {subField.answer}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </>
        ) : (
          <section className="provider-directory-summary">
            <h2 className="provider-directory-name">Provider Directory</h2>
            <p className="provider-directory-info-text mb-0">
              {loadError || "No provider details were returned for this selection."}
            </p>
          </section>
        )}
      </div>
    </>
  );
};

export default ProviderDirectory;
